import { CHANNELS } from './channelConfig';
import CampaignPreview from './CampaignPreview';
import CampaignImageField from './CampaignImageField';
import {
  DEFAULT_STOREFRONT_OBJECT_POSITION,
  sanitizeStorefrontObjectPosition,
} from './storefrontObjectPosition';

const emptyForm = () => ({
  title: '',
  subject: '',
  message: '',
  channels: ['IN_APP'],
  imageUrl: '',
  ctaLabel: '',
  ctaUrl: '',
  destinatairesType: 'ALL',
  destinatairesIds: undefined,
  sourceOpportunity: null,
  testEmail: '',
  storefrontEnabled: false,
  storefrontDurationPreset: '14',
  storefrontCustomDays: 14,
  storefrontObjectPosition: { ...DEFAULT_STOREFRONT_OBJECT_POSITION },
});

/**
 * Hydrate le formulaire depuis une opportunité (Vague 2) ou une ouverture manuelle.
 */
export function applyInitialCampaign(initial) {
  const base = emptyForm();
  if (!initial || typeof initial !== 'object') {
    return { ...base, channels: ['IN_APP'] };
  }
  const channels = Array.isArray(initial.channels) && initial.channels.length
    ? initial.channels.filter((c) => ['IN_APP', 'EMAIL'].includes(c))
    : ['IN_APP'];
  return {
    ...base,
    title: initial.title != null ? String(initial.title) : '',
    subject: initial.subject != null ? String(initial.subject) : '',
    message: initial.message != null ? String(initial.message) : '',
    channels: channels.length ? channels : ['IN_APP'],
    imageUrl: initial.imageUrl != null ? String(initial.imageUrl) : '',
    ctaLabel: initial.ctaLabel != null ? String(initial.ctaLabel) : '',
    ctaUrl: initial.ctaUrl != null ? String(initial.ctaUrl) : '',
    destinatairesType: initial.destinatairesType || 'ALL',
    destinatairesIds: initial.destinatairesIds,
    sourceOpportunity: initial.sourceOpportunity != null ? String(initial.sourceOpportunity) : null,
    storefrontEnabled: initial.storefrontEnabled === true,
    storefrontDurationPreset:
      initial.storefrontDurationPreset != null
        ? String(initial.storefrontDurationPreset)
        : '14',
    storefrontCustomDays:
      initial.storefrontCustomDays != null ? Number(initial.storefrontCustomDays) || 14 : 14,
    storefrontObjectPosition: sanitizeStorefrontObjectPosition(
      initial.storefrontObjectPosition
    ),
  };
}

export { emptyForm };

export default function CampaignFormModal({
  isOpen,
  onClose,
  form,
  onChange,
  marketingEnabled,
  commerceName,
  entitlements,
  onRequestSend,
  onSendTest,
  submitting,
  testing,
  error,
  platformMode = false,
}) {
  if (!isOpen) return null;

  const toggleChannel = (id) => {
    const channel = CHANNELS.find((c) => c.id === id);
    if (!channel || channel.enabled === false) return;
    if (platformMode && id !== 'IN_APP') return;
    if (channel.requiresMarketing && !marketingEnabled) return;

    const next = form.channels.includes(id)
      ? form.channels.filter((c) => c !== id)
      : [...form.channels, id];
    onChange({ ...form, channels: next.length ? next : form.channels });
  };

  const hasEmail = !platformMode && form.channels.includes('EMAIL');
  const hasInApp = form.channels.includes('IN_APP') || platformMode;
  const remainingEmail = entitlements?.remaining?.email;
  const remainingInApp = entitlements?.remaining?.inApp;
  const hasQuotaForSelection =
    platformMode ||
    ((!hasEmail || remainingEmail > 0) && (!hasInApp || remainingInApp > 0));

  const canSubmit =
    form.channels.length > 0 &&
    form.message.trim() &&
    (hasInApp ? form.title.trim() : true) &&
    (hasEmail ? (form.subject || form.title).trim() : true);

  return (
    <div className="fixed inset-0 z-[5000] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} aria-hidden />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
            <h3 className="text-xl font-semibold text-white">Créer une campagne</h3>
            <button type="button" className="text-white/90 hover:text-white text-sm" onClick={onClose}>
              Fermer
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            <div className="lg:col-span-3 p-6 space-y-5 border-b lg:border-b-0 lg:border-r border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Canaux</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {CHANNELS.map((ch) => {
                    const blocked =
                      ch.enabled === false ||
                      (platformMode && ch.id !== 'IN_APP') ||
                      (ch.requiresMarketing && !marketingEnabled);
                    const selected = form.channels.includes(ch.id) || (platformMode && ch.id === 'IN_APP');
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        disabled={blocked}
                        onClick={() => toggleChannel(ch.id)}
                        className={`text-left rounded-lg border px-3 py-3 transition ${
                          selected && !blocked
                            ? 'border-primary-500 bg-primary-50'
                            : blocked
                              ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900 text-sm">{ch.label}</span>
                          {ch.soonLabel && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">
                              {ch.soonLabel}
                            </span>
                          )}
                          {ch.requiresMarketing && !marketingEnabled && (
                            <span className="text-[10px] uppercase tracking-wide text-sky-700">
                              Indisponible
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{ch.description}</p>
                      </button>
                    );
                  })}
                </div>
                {platformMode && (
                  <p className="mt-2 text-xs text-sky-900 bg-sky-50 border border-sky-100 rounded-md px-3 py-2">
                    Mode <strong>Plateforme Clickorico</strong> : message adressé à tous les clients
                    actifs. Pas de quota commerçant. L’email marketing boutique se gère sur la fiche
                    du commerce.
                  </p>
                )}
                {!platformMode && !marketingEnabled && (
                  <p className="mt-2 text-xs text-sky-900 bg-sky-50 border border-sky-100 rounded-md px-3 py-2">
                    L’envoi d’emails marketing est momentanément indisponible. Vous pouvez continuer
                    à envoyer des notifications dans l’application. Les emails liés aux commandes
                    restent actifs.
                  </p>
                )}
                {!platformMode && marketingEnabled && (
                  <p className="mt-2 text-xs text-gray-600">
                    Email et In-App ont des quotas mensuels <strong>séparés</strong> : chaque canal
                    activé consomme 1 unité sur son propre compteur.
                  </p>
                )}
                {!platformMode && (hasEmail || hasInApp) && (
                  <p className="mt-2 text-xs text-gray-500">
                    {hasEmail && typeof remainingEmail === 'number' && (
                      <>
                        Email : <strong>{remainingEmail}</strong> restante
                        {remainingEmail > 1 ? 's' : ''}
                        {hasInApp ? ' · ' : ''}
                      </>
                    )}
                    {hasInApp && typeof remainingInApp === 'number' && (
                      <>
                        In-App : <strong>{remainingInApp}</strong> restante
                        {remainingInApp > 1 ? 's' : ''}
                      </>
                    )}
                    . L’envoi test n’en consomme pas.
                    {!hasQuotaForSelection && (
                      <>
                        {' '}
                        Quota insuffisant sur au moins un canal sélectionné — passez à Plus ou
                        réessayez le mois prochain.
                      </>
                    )}
                  </p>
                )}
              </div>

              {hasInApp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={form.title}
                    onChange={(e) => onChange({ ...form, title: e.target.value })}
                    placeholder="Ex. Nouvelle offre du week-end"
                  />
                </div>
              )}

              {hasInApp && !platformMode && (
                <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={!!form.storefrontEnabled}
                      onChange={(e) =>
                        onChange({ ...form, storefrontEnabled: e.target.checked })
                      }
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800">
                        Afficher sur ma boutique
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">
                        Bandeau visible sur votre fiche boutique, en plus de la notification In-App.
                        Le client peut fermer le bandeau sans perdre le message dans son onglet
                        Notifications.
                      </span>
                    </span>
                  </label>

                  {form.storefrontEnabled ? (
                    <div className="pl-7 space-y-3">
                      <p className="text-xs font-medium text-gray-700">Durée d&apos;affichage</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: '3', label: '3 jours' },
                          { value: '7', label: '7 jours' },
                          { value: '14', label: '14 jours' },
                          { value: '30', label: '30 jours' },
                          { value: 'custom', label: 'Personnalisé' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              onChange({ ...form, storefrontDurationPreset: opt.value })
                            }
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                              form.storefrontDurationPreset === opt.value
                                ? 'border-primary-500 bg-primary-50 text-primary-800'
                                : 'border-gray-200 text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {form.storefrontDurationPreset === 'custom' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={90}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
                            value={form.storefrontCustomDays ?? 14}
                            onChange={(e) =>
                              onChange({
                                ...form,
                                storefrontCustomDays: Math.min(
                                  90,
                                  Math.max(1, Number(e.target.value) || 1)
                                ),
                              })
                            }
                          />
                          <span className="text-xs text-gray-500">jours (maximum 90)</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}

              {hasEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sujet email</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={form.subject}
                    onChange={(e) => onChange({ ...form, subject: e.target.value })}
                    placeholder="Objet visible dans la boîte mail"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-sans"
                  rows={6}
                  value={form.message}
                  onChange={(e) => onChange({ ...form, message: e.target.value })}
                  placeholder={
                    'Bonjour,\n\nVotre message ici.\n\nVous pouvez utiliser **gras** et des liens https://…\nLes paragraphes se créent avec une ligne vide.'
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Texte simple uniquement. Clickorico génère un HTML sécurisé à l’envoi — pas de HTML libre.
                </p>
              </div>

              <CampaignImageField
                imageUrl={form.imageUrl || ''}
                onChange={(url) =>
                  // Functional update: après upload, CampaignImageField reset aussi le
                  // focal — deux setState synchrone avec {…form} stale effaçaient imageUrl.
                  onChange((prev) => ({ ...prev, imageUrl: url || '' }))
                }
                showInAppFormatHint={hasInApp}
                showStorefrontFocal={hasInApp}
                storefrontObjectPosition={form.storefrontObjectPosition}
                onStorefrontObjectPositionChange={(pos) =>
                  onChange((prev) => ({ ...prev, storefrontObjectPosition: pos }))
                }
              />

              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                <p className="text-sm font-medium text-gray-800">Bouton d’action (optionnel)</p>
                <p className="text-xs text-gray-500">
                  Affiché uniquement si le libellé et le lien sont renseignés. Utile pour une offre ;
                  inutile pour une simple info (fermeture, horaires…).
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Libellé</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={form.ctaLabel || ''}
                    onChange={(e) => onChange({ ...form, ctaLabel: e.target.value })}
                    placeholder="Ex. Voir la carte"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lien</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={form.ctaUrl || ''}
                    onChange={(e) => onChange({ ...form, ctaUrl: e.target.value })}
                    placeholder="/votre-boutique ou https://…"
                  />
                </div>
              </div>

              {hasEmail && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <p className="text-sm font-medium text-gray-800">Envoi test (hors quota)</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={form.testEmail}
                      onChange={(e) => onChange({ ...form, testEmail: e.target.value })}
                      placeholder="votre@email.fr"
                    />
                    <button
                      type="button"
                      className="btn-secondary text-sm whitespace-nowrap"
                      disabled={testing || !form.testEmail || !form.message.trim()}
                      onClick={onSendTest}
                    >
                      {testing ? 'Envoi…' : 'Envoyer un test'}
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 border border-red-100 text-red-700 text-sm px-3 py-2">
                  {error}
                </div>
              )}

              {/* Aperçu mobile : sous le formulaire */}
              <div className="lg:hidden pt-2 border-t border-gray-100">
                <CampaignPreview form={form} commerceName={commerceName} />
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-2 p-6 bg-gray-50">
              <CampaignPreview form={form} commerceName={commerceName} />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-3 bg-white">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Annuler
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canSubmit || submitting || !hasQuotaForSelection}
              onClick={onRequestSend}
            >
              {submitting ? 'Préparation…' : 'Continuer vers l’envoi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
